/**
 * A team's throughput range and cap, applied in the browser to preview a
 * draft before it is saved (shipcast-o7j.7).
 *
 * Mirrors the server's `ThroughputRange::window` and `team_series`
 * (`crates/api/src/ingestion/team_throughput.rs`) — the server stays the
 * authority: saving re-cuts there, and what the row then shows is what it
 * stored. The two must agree on:
 *
 * - a rolling range of N weeks is the 7N days ending on the fetch day;
 * - a fixed range is `from..=to`, both inclusive;
 * - days of the range outside the series are absent, never invented;
 * - the cap lowers each day to at most `cap`; `null` means no cap;
 * - a range with no completion on any of its days is stored as nothing: the
 *   server keeps the old series and answers `empty_throughput`.
 */

import type { DailyThroughput, ThroughputRangeBody, ThroughputSettingsBody } from './types';

/** The largest rolling range the server accepts (`MAX_ROLLING_WEEKS`). */
export const MAX_ROLLING_WEEKS = 520;

const DAY_MS = 86_400_000;

function shiftDay(isoDate: string, days: number): string {
  return new Date(Date.parse(`${isoDate}T00:00:00Z`) + days * DAY_MS).toISOString().slice(0, 10);
}

/** The first and last day of `range`, as of the fetch day `asOf`. */
export function rangeWindow(range: ThroughputRangeBody, asOf: string): { from: string; to: string } {
  if (range.mode === 'rolling') return { from: shiftDay(asOf, -(7 * range.weeks - 1)), to: asOf };
  return { from: range.from, to: range.to };
}

export interface ThroughputCut {
  from: string;
  to: string;
  /** Per day of the whole history: inside the range, and what the cap keeps. */
  days: Array<DailyThroughput & { inRange: boolean; kept: number }>;
  /** The series the simulation would sample: in range, capped, oldest first. */
  used: number[];
}

/** Apply `settings` to the whole stored history, as the server would. */
export function cutThroughput(
  history: readonly DailyThroughput[],
  settings: ThroughputSettingsBody,
  asOf: string,
): ThroughputCut {
  const { from, to } = rangeWindow(settings.range, asOf);
  const cap = settings.cap ?? null;
  const days = history.map((d) => {
    // ISO dates compare correctly as strings.
    const inRange = d.date >= from && d.date <= to;
    return { ...d, inRange, kept: cap === null ? d.count : Math.min(d.count, cap) };
  });
  return { from, to, days, used: days.filter((d) => d.inRange).map((d) => d.kept) };
}

/**
 * Whether the server would store `used` as the team's throughput. It keeps the
 * old series instead when the range holds no day, or no completion on any day:
 * the sampler rejects both (`sampled_throughput`, shipcast-d8r.36).
 */
export function isSampleable(used: readonly number[]): boolean {
  return used.some((count) => count > 0);
}

/** Why a draft cannot be saved, or `null` when it can — the server's rules. */
export function settingsProblem(settings: ThroughputSettingsBody): string | null {
  const { range } = settings;
  if (range.mode === 'rolling') {
    if (!Number.isInteger(range.weeks) || range.weeks < 1 || range.weeks > MAX_ROLLING_WEEKS) {
      return `A rolling range needs between 1 and ${MAX_ROLLING_WEEKS} weeks.`;
    }
  } else {
    if (!range.from || !range.to) return 'A fixed range needs both a first and a last day.';
    if (range.from > range.to) return 'A fixed range must start on or before the day it ends.';
  }
  const cap = settings.cap ?? null;
  if (cap !== null && (!Number.isInteger(cap) || cap < 1)) {
    return 'The cap must be a whole number of at least 1 item per day; leave it off for no cap.';
  }
  return null;
}
